
import React from 'react';
import { useTheme } from "@/contexts/ThemeContext";

interface EngagementBarChartProps {
  value: number;
  maxValue: number;
  className?: string;
  shouldAnimate?: boolean;
}

export const EngagementBarChart = ({ 
  value, 
  maxValue, 
  className = "",
  shouldAnimate = false 
}: EngagementBarChartProps) => {
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  
  // Calculate percentage width (minimum 5% for visibility when value > 0)
  const percentage = maxValue > 0 ? Math.max((value / maxValue) * 100, value > 0 ? 5 : 0) : 0;
  
  // Generate unique animation delay for staggered effect
  const animationDelay = shouldAnimate ? Math.random() * 300 : 0;
  
  // Determine color based on engagement count
  const getBarColor = (count: number) => {
    if (count >= 21000) {
      return isLightMode ? 'bg-orange-500' : 'bg-orange-600'; // Orange for 21k+
    } else if (count >= 5000) {
      return isLightMode ? 'bg-orange-300' : 'bg-orange-400'; // Subtle orange for 5k-20k
    } else if (count >= 1) {
      return isLightMode ? 'bg-yellow-100' : 'bg-yellow-200'; // Light yellow for 1-5k
    }
    return isLightMode ? 'bg-gray-300' : 'bg-gray-600'; // Gray for 0
  };
  
  const barColor = getBarColor(value);
  
  return (
    <div className={`relative flex items-center ${className}`}>
      {/* Background bar */}
      <div className={`absolute inset-0 rounded overflow-hidden ${isLightMode ? 'bg-gray-100' : 'bg-gray-800'}`} />
      
      {/* Progress bar with color categorization and subtle stripes */}
      <div 
        className={`absolute left-0 top-0 h-full rounded overflow-hidden transition-all duration-1000 ease-out ${barColor}`}
        style={{ 
          width: shouldAnimate ? `${percentage}%` : `${percentage}%`,
          backgroundImage: isLightMode 
            ? `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 8px,
                rgba(255, 255, 255, 0.15) 8px,
                rgba(255, 255, 255, 0.15) 16px
              )`
            : `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 8px,
                rgba(255, 255, 255, 0.1) 8px,
                rgba(255, 255, 255, 0.1) 16px
              )`,
          animation: shouldAnimate 
            ? `growWidthEngagement 1000ms ease-out ${animationDelay}ms both, moveStripesEngagement 3s linear infinite`
            : 'none'
        }}
      />
      
      {/* Value text */}
      <div className={`relative z-10 px-3 py-1 font-medium ${
        isLightMode ? 'text-gray-900' : 'text-gray-100'
      }`}>
        {value.toLocaleString()}
      </div>
      
      {/* Global CSS for animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes growWidthEngagement {
            from {
              width: 0%;
            }
            to {
              width: ${percentage}%;
            }
          }
          
          @keyframes moveStripesEngagement {
            0% {
              background-position: 0 0;
            }
            100% {
              background-position: 32px 0;
            }
          }
        `
      }} />
    </div>
  );
};
